import type { EventHandler, VFC } from 'react'
import { Body, Bold, Button, ModalBody, ModalCloseButton, ModalFooter, Title } from '@panwds/react-ui'
import {
  BUTTON_CANCEL, BUTTON_NEXT,
  BUTTON_REMOVE,
  LABEL_DISPLAY_NAME,
  LABEL_IDENTITY_ADDRESS,
  LABEL_IDENTITY_INFORMATION_AND_ROLES,
  LABEL_IDENTITY_TYPE, TOOLTIP_READ_ONLY
} from '../../../../../constants/LabelConstants'
import { useContext, useMemo } from 'react'
import {
  IDENTITIES_PERMISSION_SET,
  IDENTITY_TYPES,
  LABEL_GROUP,
  LABEL_SERVICE_ACCOUNT,
  LABEL_USER
} from '../../../../../constants/IAMConstants'
import {
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT
} from '../../../../../../common/constants/TestingDataIds'
import GlobalContext from '../../../../../../common/context/GlobalContext'
import UserRolesTable from './UserRolesTable'
import useReadOnly from '../../../../../../common/hooks/useReadOnly'
import TooltipButton from '../../../../../../common/components/tooltipButton/TooltipButton'

const IdentityInformation: VFC<{
  member: any,
  isLoading: boolean,
  onRemove: EventHandler<any>,
  onBackPressed: EventHandler<any>,
  onNextPressed: EventHandler<any>,
  length: Number
}> = ({
  member,
  isLoading,
  onRemove,
  onBackPressed,
  onNextPressed,
  length
}) => {

  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode: tenant } = treeReducer
  const readOnly = useReadOnly(tenant?.id, IDENTITIES_PERMISSION_SET)

  const typeLabel = useMemo(() => {
    switch (member?.identity_type) {
      case IDENTITY_TYPES.SERVICE_ACCOUNT:
        return LABEL_SERVICE_ACCOUNT
      case IDENTITY_TYPES.GROUP:
        return LABEL_GROUP
      case IDENTITY_TYPES.USER:
      default:
        return LABEL_USER
    }
  }, [member])

  const removeDisabled = useMemo(() => {
    return readOnly || isLoading || member?.inherited_from?.some((id: string) => id !== tenant?.id) || length <= 1
  }, [readOnly, isLoading, member?.inherited_from, tenant?.id, length])

  return (
    <ModalBody addClassName='tw-max-h-full tw-relative tw-h-full tw-w-full'>
      <div className='tw-absolute tw-p-4 tw-flex tw-flex-col' style={{ left: 0, right: 0, top: 0, bottom: '43px' }}>
        <Title level={2} addClassName="tw-font-semibold">{LABEL_IDENTITY_INFORMATION_AND_ROLES}</Title>
        <div className='tw-pt-3'>
          <div className='tw-grid tw-grid-rows-1 tw-gap-2' style={{ gridTemplateColumns: '0.5fr minmax(0, 1fr) minmax(0, 0.75fr)' }}>
            <div className='tw-flex tw-flex-col tw-gap-1'>
              <Bold>{LABEL_IDENTITY_TYPE}</Bold>
              <Body>{typeLabel}</Body>
            </div>
            <div className='tw-flex tw-flex-col tw-gap-1'>
              <Bold>{LABEL_IDENTITY_ADDRESS}</Bold>
              <Body truncate>{member?.identity_email}</Body>
            </div>
            <div className='tw-flex tw-flex-col tw-gap-1'>
              <Bold>{LABEL_DISPLAY_NAME}</Bold>
              <Body truncate>{member.display_name?.trim() || member?.identity_email}</Body>
            </div>
          </div>
        </div>
        <div className='tw-pt-3 tw-flex-1'>
          <UserRolesTable member={member} />
        </div>
      </div>
      <ModalFooter style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '43px' }}>
        {readOnly ? (
          <TooltipButton
            label={ TOOLTIP_READ_ONLY }
            disabled
            tooltipProps={{ defaultPlacement: 'bottom-end' }}
            buttonProps = {{ appearance: 'secondary-destructive' }}>
            {BUTTON_REMOVE}
          </TooltipButton>
        ) : (
          <ModalCloseButton
            appearance="secondary-destructive"
            onClick={onRemove}
            disabled={removeDisabled}
          >{BUTTON_REMOVE}</ModalCloseButton>
        )}
        <Button
          data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
          onClick={onBackPressed}
          appearance="secondary"
        >{BUTTON_CANCEL}</Button>
        <Button
          onClick={onNextPressed}
          appearance='primary'
          data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
          disabled={isLoading}
        >{BUTTON_NEXT}</Button>
      </ModalFooter>
    </ModalBody>
  )
}

export default IdentityInformation
